import { Container } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import { Link } from 'gatsby';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ForbesLogo from '../images/forbes_partner_logo.png';
import AwardLogo from '../images/award_logo.png';
import {
  BLOG_PAGE,
  CONTACT_US_PAGE,
  FB,
  HOME_PAGE,
  LINKEDIN,
  PRICING,
  PRIVACY_PAGE,
  PRODUCTS_PAGE,
  REFUND_PAGE,
  TERMS_PAGE,
  TWITTER,
  WHITE_LABEL_PAGE,
  YOUTUBE
} from '../links';
import Logo from './icons/logo-text';
import FacebookIcon from './icons/social-icons/fb';
import LinkedInIcon from './icons/social-icons/linkedin';
import TwitterIcon from './icons/social-icons/twitter';
import YouTubeIcon from './icons/social-icons/youtube';
import roarLogoFooter from '../images/roar_logo_footer.svg';

const useStyles = makeStyles(theme => ({
  root: {
    color: theme.colors.footerColor,
    backgroundColor: theme.colors.footerBackgroundColor
  },
  content: {
    padding: theme.spacing(8, 0),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(11.25, 0)
    }
  },
  menuListTitle: {
    fontSize: 16,
    textTransform: 'uppercase',
    color: 'inherit',
    marginBottom: theme.spacing(6),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(4.25),
      fontSize: 14,
      lineHeight: '16px'
    }
  },
  menuList: {
    margin: 0,
    paddingLeft: 0,
    listStyle: 'none inside none',
    '& li:not(:last-of-type)': {
      marginBottom: theme.spacing(4)
    }
  },
  link: {
    color: theme.palette.primary.main,
    '& svg': {
      fontSize: 36,
      [theme.breakpoints.up('sm')]: {
        fontSize: 28
      }
    }
  },
  linkDisabled: {
    opacity: 0.5,
    pointerEvents: 'none'
  },
  iconsBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      textAlign: 'left'
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start'
    }
  },
  forbesLogoWrap: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  awardLogoWrap: {
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      marginLeft: 50
    }
  },
  forbesLogo: {
    display: 'block',
    width: 172,
    height: 172,
    marginTop: theme.spacing(6),
    [theme.breakpoints.up('sm')]: {
      marginTop: 0
    }
  },
  awardLogo: {
    display: 'block',
    height: 110,
    marginTop: theme.spacing(6),
    [theme.breakpoints.up('sm')]: {
      marginTop: 10,
      marginLeft: -50
    }
  },
  logo: {
    display: 'inline-block',
    marginBottom: theme.spacing(6),
    color: 'inherit',
    '& svg': {
      height: 46,
      fontSize: 75
    },
    [theme.breakpoints.up('md')]: {
      marginBottom: 0
    }
  },
  logoFooter: {
    width: 75,
    height: 46
  },
  copyrightWrap: {
    padding: theme.spacing(4, 0),
    backgroundColor: '#00171F',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(13, 10)
    }
  },
  copyrightContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.common.white,
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between'
    }
  },
  copyright: {
    fontSize: 13,
    lineHeight: '15px',
    textAlign: 'center'
  }
}));

function Footer() {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <footer className={classes.root}>
      <Container maxWidth="xl">
        <div className={classes.content}>
          <Grid container spacing={6}>
            {/* Menu */}
            <Grid item xs={6} sm={4} md={3} lg>
              <Typography variant="h4" className={classes.menuListTitle}>
                {t('footerMenu.About_ROAR')}
              </Typography>
              <ul className={classes.menuList}>
                <li>
                  <Link to="/#use-cases" className={classes.link}>
                    {t('footerMenu.Industries')}
                  </Link>
                </li>
                <li>
                  <Link to={PRODUCTS_PAGE} className={classes.link}>
                    {t('footerMenu.Products')}
                  </Link>
                </li>
                <li>
                  <a
                    href={PRICING}
                    className={classes.link}
                    rel="noopener noreferrer"
                  >
                    {t('footerMenu.Pricing')}
                  </a>
                </li>
                <li>
                  <Link to={WHITE_LABEL_PAGE} className={classes.link}>
                    {t('footerMenu.White_Label')}
                  </Link>
                </li>
                <li>
                  <Link to="/#brands-new-roar" className={classes.link}>
                    {t('footerMenu.Customers')}
                  </Link>
                </li>
                <li>
                  <Link to={BLOG_PAGE} className={classes.link}>
                    {t('footerMenu.Blog')}
                  </Link>
                </li>
              </ul>
            </Grid>

            {/* Terms and Policy */}
            <Grid item xs={6} sm={4} md={3} lg>
              <Typography variant="h4" className={classes.menuListTitle}>
                {t('footerMenu.Legal')}
              </Typography>
              <ul className={classes.menuList}>
                <li>
                  <Link to={TERMS_PAGE} className={classes.link}>
                    {t('footerMenu.Terms_of_use')}
                  </Link>
                </li>
                <li>
                  <Link to={PRIVACY_PAGE} className={classes.link}>
                    {t('footerMenu.Privacy_Policy')}
                  </Link>
                </li>
                <li>
                  <Link to={REFUND_PAGE} className={classes.link}>
                    {t('footerMenu.Refund_Policy')}
                  </Link>
                </li>
              </ul>
            </Grid>

            {/* Contact us, demo, download */}
            <Grid item xs={6} sm={4} md={3} lg>
              <Typography variant="h4" className={classes.menuListTitle}>
                {t('footerMenu.Sales')}
              </Typography>
              <ul className={classes.menuList}>
                <li>
                  <Link to={CONTACT_US_PAGE} className={classes.link}>
                    {t('footerMenu.Contact_us')}
                  </Link>
                </li>
                <li>
                  <Link to={CONTACT_US_PAGE} className={classes.link}>
                    {t('footerMenu.Request_a_Demo')}
                  </Link>
                </li>
              </ul>
            </Grid>

            {/* Social icon-links */}
            <Grid className={classes.iconsBlock} item xs={12} md={3}>
              <div>
                <Typography variant="h4" className={classes.menuListTitle}>
                  {t('footerMenu.Follo_Us')}
                </Typography>
                <div>
                  <IconButton
                    className={classes.link}
                    href={TWITTER}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Twitter"
                  >
                    <TwitterIcon />
                  </IconButton>
                  <IconButton
                    className={classes.link}
                    href={FB}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Facebook"
                  >
                    <FacebookIcon />
                  </IconButton>
                  <IconButton
                    className={classes.link}
                    href={YOUTUBE}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="YouTube"
                  >
                    <YouTubeIcon />
                  </IconButton>
                  <IconButton
                    className={classes.link}
                    href={LINKEDIN}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="LinkedIn"
                  >
                    <LinkedInIcon />
                  </IconButton>
                </div>
              </div>
              <Hidden lgUp>
                <img
                  className={classes.forbesLogo}
                  src={ForbesLogo}
                  alt="Forbes partner logo"
                />
              </Hidden>
              <Hidden lgUp>
                <img
                  className={classes.awardLogo}
                  src={AwardLogo}
                  alt="Award logo"
                />
              </Hidden>
            </Grid>

            {/* Forbes Logo */}
            <Hidden mdDown>
              <Grid item lg>
                <div className={classes.forbesLogoWrap}>
                  <img
                    className={classes.forbesLogo}
                    src={ForbesLogo}
                    alt="Forbes partner logo"
                  />
                </div>
              </Grid>
              <Grid item lg>
                <div
                  className={clsx(
                    classes.awardLogoWrap,
                    classes.forbesLogoWrap
                  )}
                >
                  <img
                    className={classes.awardLogo}
                    src={AwardLogo}
                    alt="Award logo"
                  />
                </div>
              </Grid>
            </Hidden>
          </Grid>
        </div>
      </Container>

      {/* Copyright */}
      <div className={classes.copyrightWrap}>
        <Container maxWidth="xl">
          <div className={classes.copyrightContent}>
            <a href={HOME_PAGE} className={classes.logo}>
              <img src={roarLogoFooter} className={classes.logoFooter} />
            </a>
            <span className={classes.copyright}>
              Copyright © 2020 ROAR IO Inc - All rights reserved
            </span>
          </div>
        </Container>
      </div>
    </footer>
  );
}

export default Footer;
